/**
 * Entity-type filter — shows a checkbox per entity type present in the current
 * graph, grouped by category. Unchecking a type hides every node of that type.
 *
 * Hidden TYPES are independent of hidden individual nodes (right-click → Hide).
 * Both reduce to the same `visibleElements` filter in GraphPanel.
 *
 * Designed for analyst-driven investigation of dense graphs (a DNS attack-
 * surface graph for a single tenant can easily produce 40-60 nodes spanning
 * domain / ip / asn / application / organization — most investigations want
 * to look at one layer at a time).
 */

import { useMemo, useState } from 'react';
import { getCategoryForType, type Category } from '@cade/graph-logic';
import type { ElementDefinition } from 'cytoscape';

/** Cosmetic ordering — categories listed top-down. Matches the way analysts
 *  scan: "who/what" (identity, compute, network) before "what they're using"
 *  (application, data) before "what's wrong" (findings). */
const CATEGORY_ORDER: Category[] = [
  'identity',
  'compute',
  'network',
  'application',
  'data',
  'findings',
  'context',
  'framework',
];

const CATEGORY_LABELS: Record<Category, string> = {
  identity:    'Identity',
  compute:     'Compute',
  network:     'Network',
  application: 'Application',
  data:        'Data',
  findings:    'Findings',
  context:     'Context',
  framework:   'Framework',
};

interface Props {
  /** Full element set (pre-filter). The component derives type counts itself. */
  elements: ElementDefinition[];
  hiddenTypes: Set<string>;
  onToggleType: (type: string) => void;
  onResetTypes: () => void;
}

interface TypeStat {
  type: string;
  category: Category;
  count: number;
}

export function GraphTypeFilter({ elements, hiddenTypes, onToggleType, onResetTypes }: Props) {
  const [open, setOpen] = useState(false);

  // Aggregate type counts on the unfiltered element set so the analyst can
  // un-hide a type they previously dropped without losing the option.
  const stats = useMemo<TypeStat[]>(() => {
    const counts = new Map<string, number>();
    for (const el of elements) {
      const isNode = el.group === 'nodes' || (!el.group && !el.data.source);
      if (!isNode) continue;
      const t = (el.data.type as string) ?? 'unknown';
      counts.set(t, (counts.get(t) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([type, count]) => ({ type, count, category: getCategoryForType(type) }))
      .sort((a, b) => {
        const ca = CATEGORY_ORDER.indexOf(a.category);
        const cb = CATEGORY_ORDER.indexOf(b.category);
        if (ca !== cb) return ca - cb;
        return a.type.localeCompare(b.type);
      });
  }, [elements]);

  // IMPORTANT: keep all hooks above the early-return line. Conditional hooks
  // crash with "Rendered more hooks than during the previous render" the
  // moment the graph transitions from 1 type to many (common streaming case).
  const grouped = useMemo(() => {
    const byCategory = new Map<Category, TypeStat[]>();
    for (const s of stats) {
      const arr = byCategory.get(s.category) ?? [];
      arr.push(s);
      byCategory.set(s.category, arr);
    }
    return byCategory;
  }, [stats]);

  if (stats.length <= 1) return null; // single-type graph — filter is pointless

  const totalHidden = hiddenTypes.size;

  return (
    <div className="graph-type-filter">
      <button
        className={`topbar-icon-btn graph-type-filter-btn ${totalHidden > 0 ? 'has-active' : ''}`}
        onClick={() => setOpen(!open)}
        title={totalHidden > 0 ? `${totalHidden} entity type${totalHidden > 1 ? 's' : ''} hidden` : 'Filter by entity type'}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
        </svg>
        {totalHidden > 0 && <span className="graph-type-filter-badge">{totalHidden}</span>}
      </button>
      {open && (
        <div className="graph-type-filter-dropdown">
          <div className="graph-type-filter-header">
            <span>Entity types</span>
            {totalHidden > 0 && (
              <button className="btn-link btn-sm" onClick={() => { onResetTypes(); }}>
                Show all
              </button>
            )}
          </div>
          <div className="graph-type-filter-list">
            {CATEGORY_ORDER.filter(c => grouped.has(c)).map((category) => (
              <div key={category} className="graph-type-filter-group">
                <div className={`graph-type-filter-group-label cat-${category}`}>
                  {CATEGORY_LABELS[category]}
                </div>
                {grouped.get(category)!.map(({ type, count }) => {
                  const isHidden = hiddenTypes.has(type);
                  return (
                    <label key={type} className={`graph-type-filter-row ${isHidden ? 'is-hidden' : ''}`}>
                      <input
                        type="checkbox"
                        checked={!isHidden}
                        onChange={() => onToggleType(type)}
                      />
                      <span className="graph-type-filter-type">{type}</span>
                      <span className="graph-type-filter-count">{count}</span>
                    </label>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
